import React from 'react';
import { Helmet } from 'react-helmet';

function SEO({ 
  title = 'Remuvr - Free AI Background Removal Tool',
  description = 'Remove image backgrounds instantly with our free AI tool. Perfect for product photos, portraits, and e-commerce. No signup required.',
  path = '',
  image = '/social-media/twitter-card.png',
  url = 'https://remuvr.com',
  type = 'website',
  article = false,
  pageName = 'home',
  price = null,
  reviewCount = null,
  rating = null
}) {
  const siteMetadata = {
    siteUrl: 'https://remuvr.com',
    title: 'Remuvr - AI Background Removal Tool',
    description: 'Remove backgrounds from images instantly with our AI-powered tool. Fast, accurate, and free to try. Perfect for e-commerce, professional photos, and design work.',
    social: {
      twitter: '@remuvr'
    },
    organization: {
      name: 'Remuvr',
      logo: 'https://remuvr.com/social-media/twitter-card.png',
      logoSquare: 'https://remuvr.com/logo192.png',
      sameAs: [
        'https://twitter.com/remuvr',
        'https://www.linkedin.com/company/remuvr',
        'https://github.com/remuvr'
      ]
    }
  };

  const absoluteImageUrl = image.startsWith('http')
    ? image
    : `${siteMetadata.siteUrl}${image}`;

  // Base organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteMetadata.siteUrl}/#organization`,
    name: siteMetadata.organization.name,
    url: siteMetadata.siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: siteMetadata.organization.logo
    },
    sameAs: siteMetadata.organization.sameAs
  };

  // Base website schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteMetadata.siteUrl}/#website`,
    name: siteMetadata.title,
    url: siteMetadata.siteUrl,
    publisher: {
      '@id': `${siteMetadata.siteUrl}/#organization`
    }
  };

  // Generate page-specific schema
  const getPageSchema = () => {
    switch(pageName) {
      case 'home':
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${url}/#webpage`,
          url: url,
          name: title,
          description: description,
          isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` },
          about: { '@id': `${siteMetadata.siteUrl}/#organization` },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: absoluteImageUrl
          },
          potentialAction: {
            '@type': 'UseAction',
            target: url
          }
        };

      case 'about':
        return {
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          '@id': `${url}/#webpage`,
          url: url,
          name: title,
          description: description,
          isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` },
          about: { '@id': `${siteMetadata.siteUrl}/#organization` }
        };

      case 'contact':
        return {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          '@id': `${url}/#webpage`,
          url: url,
          name: title,
          description: description,
          isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` },
          provider: { '@id': `${siteMetadata.siteUrl}/#organization` }
        };

      case 'pricing':
        return {
          '@context': 'https://schema.org',
          '@type': 'Product',
          '@id': `${url}/#product`,
          name: 'Remuvr Background Removal Service',
          description: description,
          url: url,
          brand: {
            '@type': 'Brand',
            name: 'Remuvr'
          },
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'USD',
            lowPrice: '0',
            highPrice: price || '29.99',
            offerCount: '3'
          },
          aggregateRating: rating && reviewCount ? {
            '@type': 'AggregateRating',
            ratingValue: rating,
            reviewCount: reviewCount,
            bestRating: '5',
            worstRating: '1'
          } : undefined
        };

      case 'api-docs':
        return {
          '@context': 'https://schema.org',
          '@type': 'TechArticle',
          '@id': `${url}/#article`,
          url: url,
          name: title,
          description: description,
          isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` },
          publisher: { '@id': `${siteMetadata.siteUrl}/#organization` },
          articleBody: 'API documentation for Remuvr background removal service',
          technicalPlatform: 'REST API'
        };

      case 'results':
        return {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          '@id': `${url}/#software`,
          name: title,
          description: description,
          applicationCategory: 'ImageProcessingApplication',
          operatingSystem: 'Web browser',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          }
        };

      default:
        return {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${url}/#webpage`,
          url: url,
          name: title,
          description: description,
          isPartOf: { '@id': `${siteMetadata.siteUrl}/#website` }
        };
    }
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': siteMetadata.siteUrl,
          name: 'Home'
        }
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': url,
          name: title
        }
      }
    ]
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Remuvr",
    "url": siteMetadata.siteUrl,
    "description": description,
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "AI-powered background removal",
      "Instant processing",
      "High-quality output",
      "Free to use",
      "No signup required",
      "Bulk processing available"
    ]
  };

  return (
    <Helmet>
      {/* General tags */}
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />
      <meta name="theme-color" content="#1E3A8A" />
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={`${siteMetadata.siteUrl}${path}`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Remuvr" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={siteMetadata.social.twitter} />
      <meta name="twitter:creator" content={siteMetadata.social.twitter} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content={`${title} - Preview Image`} />
      
      {/* Keywords */}
      <meta name="keywords" content="background removal, remove background, AI background remover, free background removal, image editing, photo editor, transparent background, product photography, e-commerce photos, portrait background removal" />

      {/* Additional SEO Tags */}
      <meta name="application-name" content="Remuvr" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="googlebot" content="index, follow" />
      <meta name="google" content="notranslate" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* JSON-LD Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(getPageSchema())}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export default SEO;
